import * as RadixSlider from '@radix-ui/react-slider';
import { forwardRef, useCallback, useEffect, useState } from 'react';

import { Tooltip, type TooltipProps } from '@air/primitive-tooltip';
import { tailwindVariants, type VariantProps } from '@air/utils-tailwind';

export const slider = tailwindVariants({
  base: 'relative flex touch-none select-none items-center justify-center',
  variants: {
    orientation: {
      horizontal: 'h-4 w-full',
      vertical: 'h-full w-4',
    },
  },
});

export const sliderTrack = tailwindVariants({
  base: 'relative rounded-full bg-gray-5',
  compoundVariants: [
    {
      class: 'h-1',
      orientation: 'horizontal',
      size: 'thick',
    },
    {
      class: 'h-0.5',
      orientation: 'horizontal',
      size: 'thin',
    },
    {
      class: 'w-1',
      orientation: 'vertical',
      size: 'thick',
    },
    {
      class: 'w-0.5',
      orientation: 'vertical',
      size: 'thin',
    },
  ],
  defaultVariants: {
    size: 'thick',
  },
  variants: {
    orientation: {
      horizontal: 'w-full',
      vertical: 'h-full',
    },
    size: {
      thick: '',
      thin: '',
    },
  },
});

export const sliderRange = tailwindVariants({
  base: 'absolute rounded-full bg-teal-9',
  defaultVariants: {
    color: 'teal',
  },
  variants: {
    color: {
      grey: 'bg-gray-12',
      teal: 'ads:bg-ads-cyan-9 bg-teal-9',
    },
    orientation: {
      horizontal: 'h-full',
      vertical: 'w-full',
    },
  },
});

export type SliderTrackVariants = VariantProps<typeof sliderTrack>;

export type SliderProps = RadixSlider.SliderProps &
  SliderTrackVariants & {
    formatTooltip?: (values: number[]) => string;
    tooltipPosition?: TooltipProps['side'];
    tooltipDisplayStrategy?: 'on-drag' | 'always';
    rangeColor?: 'teal' | 'grey';
    showRange?: boolean;
    showTooltip?: boolean; // External control for tooltip visibility
  };

export const Slider = forwardRef<HTMLSpanElement, SliderProps>(
  (
    {
      className,
      formatTooltip,
      orientation = 'horizontal',
      tooltipPosition = 'right',
      tooltipDisplayStrategy = 'always',
      value,
      rangeColor,
      showRange = true,
      showTooltip: externalShowTooltip,
      size = 'thick',
      ...restOfProps
    }: SliderProps,
    ref,
  ) => {
    const [isPointerDown, setIsPointerDown] = useState(false);
    const [hasBeenDragged, setHasBeenDragged] = useState(false);
    const [internalShowTooltip, setInternalShowTooltip] = useState(false);

    const shouldShowTooltip = tooltipDisplayStrategy === 'on-drag' ? isPointerDown : hasBeenDragged;

    useEffect(() => {
      const handlePointerMove = () => {
        if (tooltipDisplayStrategy === 'on-drag') {
          setInternalShowTooltip(true);
        }
      };

      const handlePointerUp = () => {
        setIsPointerDown(false);
        if (tooltipDisplayStrategy === 'on-drag') {
          setInternalShowTooltip(false);
        }
      };

      if (isPointerDown) {
        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
      }

      return () => {
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerUp);
      };
    }, [isPointerDown, tooltipDisplayStrategy]);

    useEffect(() => {
      setInternalShowTooltip(shouldShowTooltip);
    }, [shouldShowTooltip]);

    const finalShowTooltip = externalShowTooltip !== undefined ? externalShowTooltip : internalShowTooltip;

    const handlePointerDown = useCallback(() => {
      setIsPointerDown(true);
      if (tooltipDisplayStrategy === 'always') {
        setHasBeenDragged(true);
      }
    }, [tooltipDisplayStrategy]);

    return (
      <RadixSlider.Root
        className={slider({ className, orientation })}
        onPointerDown={handlePointerDown}
        orientation={orientation}
        ref={ref}
        value={value}
        {...restOfProps}
      >
        <RadixSlider.Track className={sliderTrack({ orientation, size })}>
          {showRange && <RadixSlider.Range className={sliderRange({ color: rangeColor, orientation })} />}
        </RadixSlider.Track>
        <Tooltip
          delayDuration={0}
          label={!!value && !!formatTooltip && <div className="text-12">{formatTooltip?.(value)}</div>}
          open={finalShowTooltip}
          side={tooltipPosition}
          sideOffset={2}
        >
          <RadixSlider.Thumb
            className="block size-2 cursor-pointer rounded-4 bg-white shadow-popover outline-none"
            data-testid="SLIDER_THUMB"
          />
        </Tooltip>
      </RadixSlider.Root>
    );
  },
);

Slider.displayName = 'Slider';
